import type { OrgMemberSummary, OrgUnitSummary } from '@nadoutong/contracts';
import { useState } from 'react';
import { useOrgMembers, useOrgUnits } from '../../api/useOrgs.ts';
import { PanelError } from '../../components/PanelError.tsx';
import { hasPermission, useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, Chip, EmptyState, PageHeader, Panel, TableSkeleton } from '../../ui/index.ts';
import { ModelPermissionsDrawer } from './ModelPermissionsDrawer.tsx';
import type { ModelPermissionTarget } from './ModelPermissionsDrawer.tsx';
import { formatTeamApiError, modeChipTone, modeLabelKey } from './shared.ts';

/**
 * `/team/permissions` — model permissions per member and per org unit
 * (epic #897 PR 7). Two independent panels (members / 部门), each row
 * carrying its current mode chip; 「配置」 opens the parameterized
 * `ModelPermissionsDrawer`. The two panels load and fail independently.
 */

export function TeamPermissions(): JSX.Element {
  const t = useT();
  const { user } = useSession();
  const orgId = user?.orgId ?? undefined;
  const canReadMembers = hasPermission(user, 'org:members:read');
  const canManage = hasPermission(user, 'org:members:manage');

  const membersQ = useOrgMembers(canReadMembers ? orgId : undefined);
  const unitsQ = useOrgUnits(canReadMembers ? orgId : undefined);
  const [target, setTarget] = useState<ModelPermissionTarget | null>(null);

  if (!canReadMembers) {
    return (
      <section data-testid="team-permissions-page" className="flex flex-col gap-4">
        <PageHeader title={t('team.permissions.title')} testId="team-permissions-header" />
        <Panel caption={t('team.permissions.title')} padded>
          <p className="m-0 text-dense text-paper-low" data-testid="team-forbidden">
            {t('team.forbidden')}
          </p>
        </Panel>
      </section>
    );
  }

  const memberRows = (membersQ.data?.members ?? []).filter((member) => member.role === 'member');
  const units = unitsQ.data?.units ?? [];

  return (
    <section data-testid="team-permissions-page" className="flex flex-col gap-4">
      <PageHeader
        title={t('team.permissions.title')}
        sub={t('team.permissions.subtitle')}
        testId="team-permissions-header"
      />

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Panel
          caption={t('team.permissions.members.title')}
          count={membersQ.data === undefined ? undefined : memberRows.length}
          testId="team-permissions-members"
        >
          {membersQ.isLoading ? (
            <TableSkeleton rows={3} testId="team-permissions-members-loading" />
          ) : membersQ.isError ? (
            <div className="p-4">
              <PanelError
                message={formatTeamApiError(
                  membersQ.error,
                  t('team.permissions.error.members_load'),
                )}
                retryLabel={t('team.retry')}
                onRetry={() => {
                  void membersQ.refetch();
                }}
                testId="team-permissions-members-error"
              />
            </div>
          ) : memberRows.length === 0 ? (
            <EmptyState
              testId="team-permissions-members-empty"
              title={t('team.permissions.members.empty')}
            />
          ) : (
            <ul className="m-0 flex list-none flex-col p-0">
              {memberRows.map((member) => (
                <PermissionRow
                  key={member.accountId}
                  title={member.displayName ?? member.email}
                  detail={member.email}
                  mode={member.modelPermissionMode}
                  canManage={canManage}
                  configureLabel={t('team.permissions.configure')}
                  modeLabel={t(modeLabelKey(member.modelPermissionMode))}
                  rowTestId={`team-permissions-member-${member.accountId}`}
                  buttonTestId={`team-permissions-configure-member-${member.accountId}`}
                  onConfigure={() => setTarget(memberTarget(member))}
                />
              ))}
            </ul>
          )}
        </Panel>

        <Panel
          caption={t('team.permissions.units.title')}
          count={unitsQ.data === undefined ? undefined : units.length}
          testId="team-permissions-units"
        >
          {unitsQ.isLoading ? (
            <TableSkeleton rows={3} testId="team-permissions-units-loading" />
          ) : unitsQ.isError ? (
            <div className="p-4">
              <PanelError
                message={formatTeamApiError(unitsQ.error, t('team.permissions.error.units_load'))}
                retryLabel={t('team.retry')}
                onRetry={() => {
                  void unitsQ.refetch();
                }}
                testId="team-permissions-units-error"
              />
            </div>
          ) : units.length === 0 ? (
            <EmptyState
              testId="team-permissions-units-empty"
              title={t('team.permissions.units.empty')}
            />
          ) : (
            <ul className="m-0 flex list-none flex-col p-0">
              {units.map((unit) => (
                <PermissionRow
                  key={unit.id}
                  title={unit.name}
                  detail={unit.path}
                  mode={unit.modelPermissionMode}
                  canManage={canManage}
                  configureLabel={t('team.permissions.configure')}
                  modeLabel={t(modeLabelKey(unit.modelPermissionMode))}
                  rowTestId={`team-permissions-unit-${unit.id}`}
                  buttonTestId={`team-permissions-configure-unit-${unit.id}`}
                  onConfigure={() => setTarget(unitTarget(unit))}
                />
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {target !== null && orgId !== undefined ? (
        <ModelPermissionsDrawer orgId={orgId} target={target} onClose={() => setTarget(null)} />
      ) : null}
    </section>
  );
}

function memberTarget(member: OrgMemberSummary): ModelPermissionTarget {
  return { kind: 'member', member };
}

function unitTarget(unit: OrgUnitSummary): ModelPermissionTarget {
  return { kind: 'unit', unit };
}

interface PermissionRowProps {
  readonly title: string;
  readonly detail: string;
  readonly mode: OrgMemberSummary['modelPermissionMode'];
  readonly modeLabel: string;
  readonly canManage: boolean;
  readonly configureLabel: string;
  readonly rowTestId: string;
  readonly buttonTestId: string;
  readonly onConfigure: () => void;
}

function PermissionRow({
  title,
  detail,
  mode,
  modeLabel,
  canManage,
  configureLabel,
  rowTestId,
  buttonTestId,
  onConfigure,
}: PermissionRowProps): JSX.Element {
  return (
    <li
      data-testid={rowTestId}
      className="flex items-center justify-between gap-3 border-b border-line-soft px-4 py-3 last:border-b-0"
    >
      <span className="min-w-0">
        <span className="block truncate text-dense font-medium text-paper">{title}</span>
        <span className="block truncate font-mono text-micro text-paper-low">{detail}</span>
      </span>
      <span className="flex shrink-0 items-center gap-2">
        <Chip tone={modeChipTone(mode)} lamp={false} testId={`${rowTestId}-mode`}>
          {modeLabel}
        </Chip>
        <Button
          variant="ghost"
          size="sm"
          disabled={!canManage}
          onClick={onConfigure}
          data-testid={buttonTestId}
        >
          {configureLabel}
        </Button>
      </span>
    </li>
  );
}
